<template>
  <div class="company-style-page">
    <!-- 页面头部 -->
    <div class="page-header page-header-4">
      <div class="container">
        <h1 class="page-title">{{ t('menu.culture_showcase') }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 切换按钮 -->
        <div class="section-switcher">
          <div
            v-for="(btn, index) in sectionButtons"
            :key="index"
            class="section-switch-btn"
            :class="{ active: activeSection === index }"
            @click="switchSection(index)"
          >
            {{ btn }}
          </div>
        </div>

        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchCurrentSectionData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 内容区域 -->
        <div v-else class="content-wrapper">
          <!-- 当前栏目的富文本内容 -->
          <div
            v-if="currentRichtext && currentRichtext.trim()"
            class="richtext-content"
            v-html="currentRichtext"
          ></div>

          <!-- 图片展示区域 -->
          <div v-if="currentImageList && currentImageList.length > 0" class="images-section">
            <!-- 照片墙 -->
            <h2 style="text-align: center;padding-bottom: 40px;">{{ t('common.photo_wall') }}</h2>
            <div class="photo-wall">

              <div
                v-for="(image, index) in currentImageList"
                :key="index"
                class="photo-wall-item"
                @click="openImagePreview(index)"
              >
                <div class="photo-wall-image-container">
                  <img
                    :src="image.path"
                    :alt="image.name"
                    class="photo-wall-image"
                    @error="handleImageError"
                  />
                  <div class="photo-wall-overlay">
                    <el-icon size="32" color="white"><ZoomIn /></el-icon>
                  </div>
                </div>
                <!-- 暂不显示图片名称 -->
                <!-- <div v-if="image.name" class="photo-wall-caption">
                  {{ image.name }}
                </div> -->
              </div>
            </div>

            <!-- 图片墙翻页 -->
            <div v-if="currentImageTotal > imagePageSize" class="image-pagination">
              <el-pagination
                background
                layout="prev, pager, next"
                :total="currentImageTotal"
                :page-size="imagePageSize"
                :current-page="currentImagePage"
                @current-change="handleImagePageChange"
              />
            </div>
          </div>

          <!-- 空状态（当没有任何内容时显示） -->
          <div
            v-if="!currentRichtext?.trim() && !currentImageList?.length"
            class="empty-state"
          >
            <div class="empty-icon">
              <el-icon size="80" color="var(--el-color-primary-light-8)">
                <Picture />
              </el-icon>
            </div>
            <h3 class="empty-title">{{ t('common.no_data') }}</h3>
            <p class="empty-description">{{ t('common.no_content') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <el-dialog
      v-model="previewVisible"
      title=""
      width="80%"
      top="5vh"
      class="image-preview-dialog"
      destroy-on-close
    >
      <div class="preview-container">
        <img
          :src="previewImageUrl"
          :alt="t('common.image_preview')"
          class="preview-image"
        />
        <div class="preview-nav">
          <button
            v-if="currentImageList.length > 1"
            class="preview-arrow prev-arrow"
            @click="prevPreview"
            :disabled="previewIndex === 0"
          >
            <el-icon size="24"><ArrowLeft /></el-icon>
          </button>
          <button
            v-if="currentImageList.length > 1"
            class="preview-arrow next-arrow"
            @click="nextPreview"
            :disabled="previewIndex === currentImageList.length - 1"
          >
            <el-icon size="24"><ArrowRight /></el-icon>
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { Warning, ArrowLeft, ArrowRight, Picture, Grid, ZoomIn } from '@element-plus/icons-vue'
import { getSinglePageContent, getImageWallList } from "@/assets/api/website";

// 使用 i18n
const { locale, t } = useI18n()

// 响应式数据
const pageData = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const activeSection = ref(0)
const currentIndex = ref(0)
const autoPlayTimer = ref(null)
const isAutoPlaying = ref(true)
const displayMode = ref('carousel') // 'carousel' 或 'wall'
const previewVisible = ref(false)
const previewIndex = ref(0)

// 图片墙分页状态：按栏目 key 缓存 { list, total, page }
const imagePageSize = ref(9)
const sectionImageState = ref({})

// 栏目配置（固定两个栏目）
const sectionConfig = [
  { key: 'diversification', path: '/diversification', labelKey: 'company_style.diversification' },
  { key: 'company-style', path: '/company-style', labelKey: 'company_style.company_style' }
]

// 计算属性
const pageTitle = computed(() => {
  return pageData.value?.page_name || t('menu.company_style')
})

const sectionButtons = computed(() => [
  t('company_style.diversification'),
  t('company_style.company_style')
])

const currentSectionData = computed(() => {
  const key = sectionConfig[activeSection.value]?.key
  return pageData.value?.[key] || null
})

const currentRichtext = computed(() => {
  return currentSectionData.value?.content?.richtext || ''
})

// 图片墙：来自独立接口（pizu_image_wall 表），与富文本分离
const currentImageState = computed(() => {
  const key = sectionConfig[activeSection.value]?.key
  return sectionImageState.value[key] || null
})

const currentImageList = computed(() => {
  return currentImageState.value?.list || []
})

const currentImageTotal = computed(() => {
  return currentImageState.value?.total || 0
})

const currentImagePage = computed(() => {
  return currentImageState.value?.page || 1
})

// 预览相关计算属性
const previewImageUrl = computed(() => {
  return currentImageList.value[previewIndex.value]?.path || ''
})

// 放大预览时暂不显示图片名称
// const previewTitle = computed(() => {
//   return currentImageList.value[previewIndex.value]?.name || t('common.image_preview')
// })

// 切换栏目
const switchSection = (index) => {
  if (index === activeSection.value) return
  activeSection.value = index
  currentIndex.value = 0
  stopAutoPlay()
  fetchCurrentSectionData()
}

// 获取当前栏目数据
const fetchCurrentSectionData = async () => {
  const sectionIdx = activeSection.value
  const config = sectionConfig[sectionIdx]

  if (!pageData.value) pageData.value = {}

  isLoading.value = true
  errorMessage.value = ''

  try {
    // 1. 富文本内容（仅提取 content 字段，不再包含图片）
    if (!pageData.value[config.key]) {
      const currentLocale = locale.value
      const response = await getSinglePageContent({
        lang: currentLocale,
        path: config.path
      })
      pageData.value[config.key] = response || null
    }

    // 2. 图片墙（独立接口，切换 tab 时始终重新加载，避免重构后数据不同步）
    await fetchSectionImages(sectionIdx, 1)
  } catch (error) {
    console.error(`获取${config.labelKey}数据失败:`, error)
    errorMessage.value = t('common.load_failed')
  } finally {
    isLoading.value = false
  }
}

// 获取指定栏目的图片墙（分页）
const fetchSectionImages = async (sectionIdx, page) => {
  const config = sectionConfig[sectionIdx]
  try {
    const res = await getImageWallList({
      module: 'pizu_single_page',
      path: config.path,
      page,
      size: imagePageSize.value
    })
    sectionImageState.value[config.key] = {
      list: res?.list || [],
      total: res?.total || 0,
      page
    }
  } catch (error) {
    console.error('获取图片墙数据失败:', error)
    sectionImageState.value[config.key] = { list: [], total: 0, page }
  }
}

// 图片墙翻页
const handleImagePageChange = (page) => {
  previewIndex.value = 0
  fetchSectionImages(activeSection.value, page)
}

// 轮播图方法
const nextSlide = () => {
  if (currentIndex.value < currentImageList.value.length - 1) {
    currentIndex.value++
  } else {
    currentIndex.value = 0
  }
  resetAutoPlay()
}

const prevSlide = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  } else {
    currentIndex.value = currentImageList.value.length - 1
  }
  resetAutoPlay()
}

const goToSlide = (index) => {
  currentIndex.value = index
  resetAutoPlay()
}

// 自动播放
const startAutoPlay = () => {
  if (currentImageList.value.length <= 1) return
  if (displayMode.value !== 'carousel') return

  clearInterval(autoPlayTimer.value)
  autoPlayTimer.value = setInterval(() => {
    if (isAutoPlaying.value) {
      nextSlide()
    }
  }, 5000)
}

const stopAutoPlay = () => {
  isAutoPlaying.value = false
  clearInterval(autoPlayTimer.value)
}

const resetAutoPlay = () => {
  stopAutoPlay()
  if (displayMode.value === 'carousel') {
    setTimeout(() => {
      isAutoPlaying.value = true
      startAutoPlay()
    }, 100)
  }
}

// 图片加载失败处理
const handleImageError = (event) => {
  event.target.style.display = 'none'
  const placeholder = document.createElement('div')
  placeholder.className = 'image-error-placeholder'
  placeholder.innerHTML = `
    <div class="placeholder-icon">
      <el-icon size="40" color="var(--el-color-primary-light-6)">
        <Picture />
      </el-icon>
    </div>
    <p class="placeholder-text">${t('common.image_load_failed')}</p>
  `
  event.target.parentElement.appendChild(placeholder)
}

// 图片预览
const openImagePreview = (index) => {
  previewIndex.value = index
  previewVisible.value = true
}

const nextPreview = () => {
  if (previewIndex.value < currentImageList.value.length - 1) {
    previewIndex.value++
  }
}

const prevPreview = () => {
  if (previewIndex.value > 0) {
    previewIndex.value--
  }
}

// 监听显示模式变化
watch(displayMode, (newMode) => {
  if (newMode === 'carousel') {
    currentIndex.value = 0
    nextTick(() => {
      startAutoPlay()
    })
  } else {
    stopAutoPlay()
  }
})

// 监听当前栏目图片变化
watch(currentImageList, (newList) => {
  currentIndex.value = 0
  if (newList?.length > 0 && displayMode.value === 'carousel') {
    nextTick(() => {
      startAutoPlay()
    })
  }
})

// 监听语言变化，重新加载数据
watch(locale, () => {
  // 语言变化时清空 pageData，重新获取当前栏目
  pageData.value = {}
  fetchCurrentSectionData()
})

// 组件挂载时获取数据
onMounted(() => {
  fetchCurrentSectionData()
})

// 组件卸载时清理定时器
onUnmounted(() => {
  stopAutoPlay()
  clearInterval(autoPlayTimer.value)
})
</script>

<style scoped lang="scss">
@import '@/assets/css/common';

.company-style-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f9fbf7 0%, #ffffff 100%);
}

// 页面头部样式
.page-header {
  padding: 200px 0 100px;
  background: url('@/assets/img/header.jpg') center center / cover no-repeat;
  position: relative;
  border-bottom: 1px solid rgba($primary-color, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: inherit;
    filter: blur(4px);
    z-index: 1;
    transform: scale(1.01);
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 140px 0 60px;
  }
}

.page-title {
  font-family: $font-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  text-align: center;
  margin-bottom: 20px;
  letter-spacing: 2px;
  text-shadow: 0 1px 2px $primary-color;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin-bottom: 16px;
  }
}

.header-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, $primary-color, $secondary-color);
  margin: 0 auto;
  border-radius: 2px;

  @media (max-width: 480px) {
    width: 60px;
    height: 3px;
  }
}

// 主要内容区域
.page-content {
  padding: 60px 0;

  @media (max-width: 768px) {
    padding: 40px 0;
  }

  @media (max-width: 480px) {
    padding: 30px 0;
  }
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 60px;
}

// 栏目切换按钮
.section-switcher {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px 30px;
  border-bottom: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 16px 24px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 12px 20px;
    gap: 8px;
  }
}

.section-switch-btn {
  padding: 10px 20px;
  background: white;
  border: 1px solid rgba($primary-color, 0.4);
  border-radius: 8px;
  color: #333;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba($primary-color, 0.7);
    background: rgba($primary-color, 0.05);
    transform: translateY(-2px);
  }

  &.active {
    background: $primary-color;
    color: white;
    border-color: $primary-color;
    box-shadow: 0 4px 8px rgba($primary-color, 0.2);
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.875rem;
  }

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 0.8125rem;
  }
}

// 富文本内容样式
.richtext-content {
  background: $white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);
  line-height: 1.8;

  p {
    color: $gray-80;
    font-size: 1.125rem;
    text-align: justify;
    text-indent: 2em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (max-width: 768px) {
    padding: 30px 24px;

    p {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    padding: 24px 20px;

    p {
      font-size: 0.9375rem;
    }
  }
}

// 图片展示区域
.images-section {
  background: rgba(166, 108, 72, 0.1);
  border-radius: 16px;
  padding: 40px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 30px 24px;
  }

  @media (max-width: 480px) {
    padding: 24px 20px;
  }
}

// 显示模式切换
.display-mode-switcher {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 30px;

  @media (max-width: 480px) {
    gap: 12px;
    margin-bottom: 24px;
  }
}

.mode-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: $gray-10;
  border: 1px solid rgba($primary-color, 0.2);
  border-radius: 8px;
  color: $text-color;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  .el-icon {
    color: $primary-color;
  }

  &:hover {
    border-color: rgba($primary-color, 0.5);
    background: rgba($primary-color, 0.05);
    transform: translateY(-2px);
  }

  &.active {
    background: $primary-color;
    color: white;
    border-color: $primary-color;
    box-shadow: 0 4px 8px rgba($primary-color, 0.2);

    .el-icon {
      color: white;
    }
  }

  @media (max-width: 768px) {
    padding: 8px 20px;
    font-size: 0.875rem;
  }

  @media (max-width: 480px) {
    padding: 6px 16px;
    font-size: 0.8125rem;
  }
}

// 轮播图样式
.carousel-container {
  width: 100%;
}

.carousel-wrapper {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.carousel-main {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
  background: $gray-10;
  min-height: 520px;

  @media (max-width: 768px) {
    min-height: 450px;
  }

  @media (max-width: 480px) {
    min-height: 350px;
  }
}

.carousel-track {
  display: flex;
  transition: transform 0.5s ease;
  height: 100%;
}

.carousel-slide {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.slide-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-container {
  width: 100%;
  max-width: 700px;
  height: 450px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    height: 380px;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    height: 280px;
    padding: 15px;
  }
}

.gallery-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  transition: transform 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
  }
}

// 图片加载失败占位符
.image-error-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: $gray-10;
  border-radius: 8px;

  .placeholder-icon {
    margin-bottom: 12px;
    opacity: 0.6;
  }

  .placeholder-text {
    font-size: 0.875rem;
    color: $gray-60;
  }
}

// 导航箭头
.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  background: rgba($white, 0.9);
  border: 1px solid rgba($primary-color, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    background: $white;
    border-color: $primary-color;
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  :deep(.el-icon) {
    color: $primary-color;
    font-weight: bold;
  }

  &.prev-arrow {
    left: 20px;
  }

  &.next-arrow {
    right: 20px;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;

    &.prev-arrow {
      left: 10px;
    }

    &.next-arrow {
      right: 10px;
    }
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;

    :deep(.el-icon) {
      font-size: 18px;
    }
  }
}

// 图片名称
.image-name {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 600;
  color: $primary-color-2;
  padding: 12px 20px;
  background: rgba($white, 0.9);
  backdrop-filter: blur(5px);
  border-top: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 10px 16px;
    bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
    padding: 8px 12px;
    bottom: 10px;
  }
}

// 缩略图导航
.thumbnail-nav {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 0 10px;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
}

.thumbnail-item {
  width: 80px;
  height: 80px;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba($primary-color, 0.3);
  }

  &.active {
    border-color: $primary-color;
    box-shadow: 0 0 0 2px rgba($primary-color, 0.2);

    .thumbnail-overlay {
      opacity: 0;
    }
  }

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
  }
}

.thumbnail-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba($black, 0.2);
  transition: opacity 0.3s ease;
}

// 照片墙样式
.photo-wall {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 16px;
  }
}

.photo-wall-item {
  cursor: pointer;
  transition: transform 0.3s ease;
  width: calc((100% - 40px) / 3);

  &:hover {
    transform: translateY(-4px);
  }

  @media (max-width: 768px) {
    width: calc((100% - 16px) / 2);
  }
}

.photo-wall-image-container {
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: 12px;
  overflow: hidden;
  background: $gray-10;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(166, 108, 72, 0.15);
}

.photo-wall-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.photo-wall-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba($primary-color, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.photo-wall-item:hover .photo-wall-overlay {
  opacity: 1;
}

.photo-wall-item:hover .photo-wall-image {
  transform: scale(1.05);
}

.photo-wall-caption {
  margin-top: 10px;
  font-size: 0.9375rem;
  color: $text-color;
  text-align: center;
  font-weight: 500;

  @media (max-width: 480px) {
    font-size: 0.875rem;
    margin-top: 8px;
  }
}

// 图片墙翻页
.image-pagination {
  display: flex;
  justify-content: center;
  margin-top: 36px;

  @media (max-width: 480px) {
    margin-top: 24px;
  }
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba($primary-color, 0.1);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1rem;
  color: $text-light;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 错误状态
.error-container {
  text-align: center;
  padding: 60px 20px;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
}

.error-icon {
  margin-bottom: 20px;
  color: #f56c6c;
}

.error-title {
  font-size: 1.5rem;
  color: #f56c6c;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.error-message {
  font-size: 1rem;
  color: $text-light;
  margin-bottom: 20px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.retry-button {
  background: $primary-color;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $primary-light-3;
    transform: translateY(-2px);
  }
}

// 空状态
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: $white;
  border-radius: 16px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 60px 20px;
  }

  @media (max-width: 480px) {
    padding: 40px 16px;
  }
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.7;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
}

.empty-title {
  font-size: 1.5rem;
  color: $text-color;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.empty-description {
  font-size: 1rem;
  color: $text-light;
  max-width: 400px;
  margin: 0 auto;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
}

// 图片预览弹窗
.preview-container {
  position: relative;
  width: 100%;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  overflow: hidden;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.preview-nav {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.preview-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  pointer-events: auto;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &.prev-arrow {
    left: 20px;
  }

  &.next-arrow {
    right: 20px;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;

    &.prev-arrow {
      left: 10px;
    }

    &.next-arrow {
      right: 10px;
    }
  }
}

:deep(.image-preview-dialog) {
  .el-dialog__header {
    padding: 16px 20px;
    border-bottom: 1px solid #eee;
  }

  .el-dialog__body {
    padding: 0;
  }
}
</style>
